import { faCamera } from '@fortawesome/free-solid-svg-icons'
import React, { useState } from 'react'
import { useQuery } from '../../../routes'
import { Avatar, Color, Indicators, Navigation } from 'ui'
import { randomColor, COLORS } from 'utils'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { UpdateAvatarModal } from './UpdateAvatarModal'
import { normalizeProfile, ProfileData } from '../../../utils/profileHelpers'
import { useQueryClient } from '@tanstack/react-query'

interface IAvatarEditorProps {
  loading?: boolean
  profile?: ProfileData | null
  profileId?: string
}

export const AvatarEditor: React.FC<IAvatarEditorProps> = ({
  loading,
  profile,
  profileId,
}) => {
  // Normalize profile data for consistent property access
  const normalizedProfile = normalizeProfile(profile)
  const queryClient = useQueryClient()
  // Manage Modal
  const { mutateAsync: updateProfile } = useUpdateProfileMutation()
  const query = useQuery<{ uploadPhoto?: boolean }>()
  const toggleModal: React.MouseEventHandler = (e) => {
    e?.preventDefault()
    query.set({ ...query.search, uploadPhoto: true })
  }
  const dismissModal = () => {
    query.set({ ...query.search, uploadPhoto: undefined })
  }

  // Update Avatar Color
  const [colorUpdating, setColorUpdating] = useState(false)
  const updateAvatarUrl = async (fileKey: string, url: string) => {
    if (!normalizedProfile) return

    // Use normalized profile id for consistency
    const userId = profileId || normalizedProfile.userId || normalizedProfile.id

    if (userId) {
      await updateProfile({
        userId: userId as string,
        avatarUrl: url,
      })
    }
  }

  const updateAvatarColor = async (color: string) => {
    if (normalizedProfile) {
      setColorUpdating(true)

      // Use normalized profile id for consistency
      const userId =
        profileId || normalizedProfile.userId || normalizedProfile.id

      await updateProfile({
        userId: userId as string,
        color,
      })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      setColorUpdating(false)
    }
  }

  // // Update Avatar Image
  // const updateAvatarUrl = async (fileKey: string) => {
  //   if (!profile) {
  //     return
  //   }
  //   await updateProfile({
  //     variables: {
  //       profileInput: {
  //         avatarKey: fileKey,
  //         id: profile.id,
  //       },
  //     },
  //   })
  // }

  return (
    <div className="flex-col flex">
      {loading ? (
        <Indicators.Spinner size="4x" />
      ) : (
        <button onClick={toggleModal as any} className="mx-auto">
          <Avatar.Simple
            color={normalizedProfile?.color ?? randomColor()}
            name={
              normalizedProfile
                ? `${normalizedProfile.firstName || ''} ${
                    normalizedProfile.lastName || ''
                  }`
                : ''
            }
            imageUrl={
              normalizedProfile ? (normalizedProfile.avatarUrl as string) : ''
            }
            maxInitials={2}
            size={128}
          />
        </button>
      )}
      <div className="mx-auto my-2 flex">
        <Color.Picker
          selectedColor={normalizedProfile?.color ?? ''}
          className={`mr-2 ${
            colorUpdating
              ? 'cursor-not-allowed opacity-50 pointer-events-none'
              : ''
          }`}
          title="Change Avatar Color"
          colors={COLORS}
          onSelectColor={updateAvatarColor}
          disabled={colorUpdating}
        />
        <Navigation.IconButton
          icon={faCamera}
          onClick={toggleModal as any}
          className="ml-2"
        />
      </div>
      {query.search.uploadPhoto ? (
        <UpdateAvatarModal
          description="Drag and drop an image below to update your profile photo."
          onDismiss={dismissModal}
          onSuccess={updateAvatarUrl}
          loading={loading || false}
          title="Update Your Profile Photo"
          profileId={profileId}
        />
      ) : null}
    </div>
  )
}
